// src/hooks/useCycleBot.js
// ============================================================
// Hook CycleBot — branché sur Redux (dispatch + selectors)
// Remplace le state local par state.cycleBot
// ============================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";

// Selectors
import {
  selectCycleBotHealth,
  selectCycleBotHealthLoading,
  selectCycleBotHealthError,
  selectCycleBotIsOnline,
  selectChatSession,
  selectChatSessionId,
  selectChatLoading,
  selectChatError,
  selectMessages,
  selectIsTyping,
  selectDashboardData,
  selectChatAlerts,
  selectJournalSummary,
  selectJournalSummaryLoading,
  selectJournalSummaryError,
  selectJournalAnalysis,
  selectJournalAnalysisLoading,
  selectJournalEntries,
  selectJournalEntriesLoading,
  selectJournalEntriesError,
  selectLogResult,
  selectLogLoading,
  selectLogError,
  selectFlowsList,
  selectFlowsListLoading,
  selectFlowsListError,
  selectFlowDetail,
  selectFlowSession,
  selectFlowSessionLoading,
  selectFlowSessionError,
  selectFlowReport,
  selectFlowReportLoading,
  selectIsGeneratingReport,
  selectPremenstrualReport,
  selectPremenstrualReportLoading,
  selectCycleReset,
  selectCycleResetLoading,
  selectJournalHistory,
  selectJournalHistoryLoading,
  selectIsFlowActive,
  selectIsFlowCompleted,
} from "../redux/selectors/cycleBotSelectors";

// Actions synchrones
import {
  addUserMessage,
  resetChat,
  resetFlowSession,
  clearError,
} from "../redux/slices/cycleBotSlice";

// Thunks
import {
  fetchCycleBotHealth,
  initCycleChat,
  sendCycleMessage,
  logCycleEntry,
  fetchJournalSummary,
  fetchJournalAnalysis,
  fetchJournalEntries,
  fetchFlowsList,
  fetchFlowDetail,
  beginCycleFlow,
  answerCycleFlow,
  fetchCycleFlowReport,
  deleteCycleFlowSession,
  fetchPremenstrualReport,
  resetCycleAtJ1,
  fetchJournalHistory,
} from "../redux/thunks/cycleBotThunks";

/* ════════════════════════════════════════════════════════════
   HOOK
════════════════════════════════════════════════════════════ */
export default function useCycleBot({ autoInit = true } = {}) {
  const dispatch    = useDispatch();
  const initialized = useRef(false);

  /* ── State depuis Redux ── */
  const health = {
    data:    useSelector(selectCycleBotHealth),
    loading: useSelector(selectCycleBotHealthLoading),
    error:   useSelector(selectCycleBotHealthError),
    isOnline:useSelector(selectCycleBotIsOnline),
  };

  const chatSession = {
    data:    useSelector(selectChatSession),
    loading: useSelector(selectChatLoading),
    error:   useSelector(selectChatError),
  };

  const messages      = useSelector(selectMessages);
  const isTyping      = useSelector(selectIsTyping);
  const dashboardData = useSelector(selectDashboardData);
  const alerts        = useSelector(selectChatAlerts);

  const journalSummary = {
    data:    useSelector(selectJournalSummary),
    loading: useSelector(selectJournalSummaryLoading),
    error:   useSelector(selectJournalSummaryError),
  };

  const journalAnalysis = {
    data:    useSelector(selectJournalAnalysis),
    loading: useSelector(selectJournalAnalysisLoading),
  };

  const journalEntries = {
    data:    useSelector(selectJournalEntries),
    loading: useSelector(selectJournalEntriesLoading),
    error:   useSelector(selectJournalEntriesError),
  };

  const logResult = {
    data:    useSelector(selectLogResult),
    loading: useSelector(selectLogLoading),
    error:   useSelector(selectLogError),
  };

  const flowsList = {
    data:    useSelector(selectFlowsList),
    loading: useSelector(selectFlowsListLoading),
    error:   useSelector(selectFlowsListError),
  };

  const flowDetail = {
    data:    useSelector(selectFlowDetail),
    loading: useSelector(selectFlowDetail),
  };

  const flowSession = {
    data:    useSelector(selectFlowSession),
    loading: useSelector(selectFlowSessionLoading),
    error:   useSelector(selectFlowSessionError),
  };

  const isGeneratingReport = useSelector(selectIsGeneratingReport);

  const flowReport = {
    data:    useSelector(selectFlowReport),
    loading: useSelector(selectFlowReportLoading),
  };

  const isFlowActive    = useSelector(selectIsFlowActive);
  const isFlowCompleted = useSelector(selectIsFlowCompleted);

  /* ════════════════════════════════════════════════════════
     ACTIONS
  ════════════════════════════════════════════════════════ */

  /* ── Health ── */
  const fetchHealth = useCallback(
    () => dispatch(fetchCycleBotHealth()),
    [dispatch]
  );

  /* ── Chat ── */
  const initChat = useCallback(
    () => dispatch(initCycleChat()),
    [dispatch]
  );

  const sendUserMessage = useCallback(
    (text) => {
      if (!chatSession.data?.session_id || !text?.trim()) return;
      // 1) Ajoute le message user immédiatement (optimistic)
      dispatch(addUserMessage(text.trim()));
      // 2) Envoie au back
      dispatch(sendCycleMessage({
        sessionId: chatSession.data.session_id,
        message:   text.trim(),
      }));
    },
    [dispatch, chatSession.data?.session_id]
  );

  const handleResetChat = useCallback(() => {
    dispatch(resetChat());
    dispatch(initCycleChat());
  }, [dispatch]);

  /* ── Journal ── */
  const logEntry = useCallback(
    async (payload) => {
      const result = await dispatch(logCycleEntry(payload));
      if (result.meta.requestStatus === "fulfilled") {
        // Rafraîchit les entrées + résumé après un log réussi
        dispatch(fetchJournalEntries(30));
        dispatch(fetchJournalEntries(90));
        dispatch(fetchJournalSummary());
      }
      return result;
    },
    [dispatch]
  );

  const fetchSummary = useCallback(
    () => dispatch(fetchJournalSummary()),
    [dispatch]
  );

  const fetchAnalysis = useCallback(
    () => dispatch(fetchJournalAnalysis()),
    [dispatch]
  );

  const fetchEntries = useCallback(
    (days = 30) => dispatch(fetchJournalEntries(days)),
    [dispatch]
  );

  /* ── Flows ── */
  const fetchFlows = useCallback(
    () => dispatch(fetchFlowsList()),
    [dispatch]
  );

  const fetchDetail = useCallback(
    (flowId) => dispatch(fetchFlowDetail(flowId)),
    [dispatch]
  );

  const beginFlow = useCallback(
    (flowId) => dispatch(beginCycleFlow(flowId)),
    [dispatch]
  );

  const answerFlow = useCallback(
    (answer) => {
      const session = flowSession.data;
      if (!session) return;

      // _flow_id et _session_id sont normalisés par le slice à chaque réponse
      const flow_id     = session._flow_id    ?? session.flow?.id ?? session.flow_id;
      const session_id  = session._session_id ?? session.session_id ?? session.id;
      const question_id = session.current_question?.id ?? session.current_question?.question_id;


      dispatch(answerCycleFlow({ flow_id, question_id, answer, session_id }));
    },
    [dispatch, flowSession.data]
  );

  const fetchReport = useCallback(
    (sessionId) => dispatch(fetchCycleFlowReport(sessionId)),
    [dispatch]
  );

  const cancelFlow = useCallback(() => {
    const sid = flowSession.data?.session_id;
    if (sid) dispatch(deleteCycleFlowSession(sid));
    else     dispatch(resetFlowSession());
  }, [dispatch, flowSession.data?.session_id]);

  const handleClearError = useCallback(
    (section) => dispatch(clearError(section)),
    [dispatch]
  );


  /* ════════════════════════════════════════════════════════
     CHAT LIMIT (localStorage, persisté par userId)
  ════════════════════════════════════════════════════════ */
  const _userId = useSelector(selectEffectiveUserId) || "guest";
  const _storageKey = `cycleflow_chat_${_userId}`;

  const _loadLimit = () => {
    try {
      const raw = localStorage.getItem(_storageKey);
      if (!raw) return { used: 0, upgraded: false, limit: 5 };
      return JSON.parse(raw);
    } catch { return { used: 0, upgraded: false, limit: 5 }; }
  };

  const [_limitState, _setLimitState] = useState(_loadLimit);

  // Sync si userId change
  useEffect(() => { _setLimitState(_loadLimit()); }, [_userId]);

  const _saveLimit = (next) => {
    try { localStorage.setItem(_storageKey, JSON.stringify(next)); } catch {}
  };

  const incrementUsed = useCallback(() => {
    _setLimitState((prev) => {
      const next = { ...prev, used: prev.used + 1 };
      _saveLimit(next);
      return next;
    });
  }, [_storageKey]);

  const doUpgrade = useCallback(() => {
    _setLimitState((prev) => {
      // Chaque upgrade ajoute 5 messages supplémentaires, sans limite de fois
      const next = {
        used:     prev.used,
        upgraded: true,           // flag Pro reste actif
        limit:    prev.limit + 5, // toujours +5 peu importe le nombre d'upgrades
      };
      _saveLimit(next);
      return next;
    });
  }, [_storageKey]);

  const resetLimit = useCallback(() => {
    const fresh = { used: 0, upgraded: false, limit: 5 };
    _saveLimit(fresh);
    _setLimitState(fresh);
  }, [_storageKey]);

  const chatLimit = {
    used:         _limitState.used,
    limit:        _limitState.limit,
    upgraded:     _limitState.upgraded,
    remaining:    Math.max(0, _limitState.limit - _limitState.used),
    limitReached: _limitState.used >= _limitState.limit,
    incrementUsed,
    doUpgrade,
    resetLimit,
    FREE_LIMIT:    5,
    UPGRADE_BONUS: 5,
  };

  const premenstrualReport = {
    data:    useSelector(selectPremenstrualReport),
    loading: useSelector(selectPremenstrualReportLoading),
  };

  const fetchPMReport = useCallback(
    ({ userId, nextJ1Date }) => dispatch(fetchPremenstrualReport({ userId, nextJ1Date })),
    [dispatch]
  );

  const cycleReset = {
    data:    useSelector(selectCycleReset),
    loading: useSelector(selectCycleResetLoading),
  };

  const journalHistory = {
    data:    useSelector(selectJournalHistory),
    loading: useSelector(selectJournalHistoryLoading),
  };

  const doResetCycleAtJ1 = useCallback(
    ({ userId, j1Date, report }) => dispatch(resetCycleAtJ1({ userId, j1Date, report })),
    [dispatch]
  );

  const fetchHistory = useCallback(
    () => dispatch(fetchJournalHistory()),
    [dispatch]
  );

  /* ════════════════════════════════════════════════════════
     AUTO-INIT (chat + flows + summary en parallèle)
  ════════════════════════════════════════════════════════ */
  const isAuthenticated = _userId !== "guest";

  useEffect(() => {
    if (!autoInit || !isAuthenticated || initialized.current) return;
    initialized.current = true;

    dispatch(initCycleChat());
    dispatch(fetchFlowsList());
    dispatch(fetchJournalSummary());
  }, [autoInit, isAuthenticated, dispatch]);

  /* ════════════════════════════════════════════════════════
     RETURN — même interface qu'avant (compatible Cycle.jsx)
  ════════════════════════════════════════════════════════ */
  return {
    // Health
    health,
    fetchHealth,

    // Chat
    chatSession,
    messages,
    isTyping,
    dashboardData,
    alerts,
    initChat,
    sendUserMessage,
    resetChat: handleResetChat,

    // Journal
    journalSummary,
    journalAnalysis,
    journalEntries,
    logResult,
    logEntry,
    fetchJournalSummary: fetchSummary,
    fetchJournalAnalysis: fetchAnalysis,
    fetchJournalEntries: fetchEntries,

    // Flows
    flowsList,
    flowDetail,
    flowSession,
    flowReport,
    isGeneratingReport,
    isFlowActive,
    isFlowCompleted,
    fetchFlows,
    fetchFlowDetail: fetchDetail,
    beginFlow,
    answerFlow,
    fetchFlowReport: fetchReport,
    cancelFlow,

    // Rapport pré-menstruel
    premenstrualReport,
    fetchPMReport,

    // Reset cycle + historique
    cycleReset,
    journalHistory,
    doResetCycleAtJ1,
    fetchHistory,

    // Utils
    clearError: handleClearError,

    // Chat Limit
    chatLimit,
  };
}
